import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "../constants";
import { FIELD_TONES } from "./FormControls";
import { FONT } from "../theme/tokens";
import {
  PROFILE_KIND_META,
  flattenGroups,
  rankSearchResults,
} from "../constants/profileSuggestions";
import { getProfileSuggestions } from "../api";

const TREND_KEY = "ap_persona_trend_v1";

function loadTrends() {
  try {
    return JSON.parse(localStorage.getItem(TREND_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function bumpTrend(kind, label) {
  try {
    const all = loadTrends();
    const bucket = all[kind] || {};
    bucket[label] = (bucket[label] || 0) + 1;
    all[kind] = bucket;
    localStorage.setItem(TREND_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

export function parseMultiValue(value) {
  return String(value || "")
    .split(/\s*;\s*|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function joinMultiValue(arr) {
  return [...new Set((arr || []).map((s) => String(s).trim()).filter(Boolean))].join("; ");
}

/**
 * Multi-select suggestion picker — Google-style search + recommendations.
 */
export default function ProfileSuggestionChips({
  kind = "interest",
  value = "",
  onChange,
  onPick,
}) {
  const meta = PROFILE_KIND_META[kind] || PROFILE_KIND_META.interest;
  const t = FIELD_TONES[meta.tone] || FIELD_TONES.blue;
  const selected = useMemo(() => parseMultiValue(value), [value]);

  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState("all");
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [trends, setTrends] = useState(() => loadTrends());
  const [remoteRecommended, setRemoteRecommended] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const bank = useMemo(() => flattenGroups(meta.groups), [meta.groups]);

  const filteredBank = useMemo(() => {
    if (activeGroup === "all") return bank;
    const g = (meta.groups || []).find((x) => x.label === activeGroup);
    return g?.options || bank;
  }, [activeGroup, bank, meta.groups]);

  const searchPool = useMemo(() => {
    const counts = trends[kind] || {};
    const pool = query.trim() ? bank : filteredBank;
    return [...pool].sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
  }, [filteredBank, bank, trends, kind, query]);

  const ranked = useMemo(
    () => rankSearchResults(kind, query, searchPool, { selected, limit: 36 }),
    [kind, query, searchPool, selected]
  );

  // Live recommendations while typing (beyond dropdown) — debounce like Google
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setRemoteRecommended([]);
      setRemoteLoading(false);
      return undefined;
    }
    if (!localStorage.getItem("token")) return undefined;
    let cancelled = false;
    setRemoteLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await getProfileSuggestions(kind, q);
        if (cancelled) return;
        const rec = Array.isArray(res?.recommended)
          ? res.recommended
          : Array.isArray(res?.suggestions)
            ? res.suggestions
            : [];
        const localSet = new Set(ranked.map((r) => r.label.toLowerCase()));
        const selSet = new Set(selected.map((s) => s.toLowerCase()));
        setRemoteRecommended(
          rec
            .map((s) => String(s || "").trim())
            .filter((s) => s && !localSet.has(s.toLowerCase()) && !selSet.has(s.toLowerCase()))
            .slice(0, 10)
        );
      } catch {
        if (!cancelled) setRemoteRecommended([]);
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    }, 320);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [kind, query, open, ranked, selected]);

  const qTrim = query.trim();
  const exactInBank = useMemo(
    () =>
      ranked.some((r) => r.label.toLowerCase() === qTrim.toLowerCase()) ||
      remoteRecommended.some((r) => r.toLowerCase() === qTrim.toLowerCase()),
    [ranked, remoteRecommended, qTrim]
  );
  const canAddCustom = Boolean(qTrim) && !selected.includes(qTrim) && !exactInBank;

  const menuRows = useMemo(() => {
    const rows = [];
    if (canAddCustom) {
      rows.push({ type: "custom", label: qTrim, display: `Add “${qTrim}”`, group: null });
    }
    remoteRecommended.forEach((label) => {
      rows.push({
        type: "recommend",
        label,
        display: label,
        group: "Recommended",
      });
    });
    ranked.forEach((r) => {
      rows.push({
        type: "option",
        label: r.label,
        display: r.label,
        group: r.group || "From list",
      });
    });
    return rows;
  }, [canAddCustom, qTrim, ranked, remoteRecommended]);

  const emit = (nextArr, picked) => {
    if (picked) {
      bumpTrend(kind, picked);
      setTrends(loadTrends());
    }
    const joined = joinMultiValue(nextArr);
    if (onChange) onChange(joined);
    else if (onPick) onPick(joined);
  };

  const toggleOption = (opt) => {
    if (selected.includes(opt)) emit(selected.filter((x) => x !== opt));
    else {
      emit([...selected, opt], opt);
      setQuery("");
      setRemoteRecommended([]);
    }
  };

  const pickRow = (row) => {
    if (!row) return;
    toggleOption(row.label);
  };

  const placeMenu = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 240);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 280 && r.top > spaceBelow;
    setMenuBox({
      left,
      width,
      top: openUp ? undefined : r.bottom + 6,
      bottom: openUp ? window.innerHeight - r.top + 6 : undefined,
      maxHeight: Math.min(320, openUp ? r.top - 16 : spaceBelow - 12),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    placeMenu();
    const onWin = () => placeMenu();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, menuRows.length, query, remoteLoading]);

  useEffect(() => {
    if (open) setActiveIdx(0);
  }, [open, query, activeGroup, remoteRecommended.length]);

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(menuRows.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (menuRows[activeIdx]) pickRow(menuRows[activeIdx]);
      else if (qTrim) {
        emit([...selected, qTrim], qTrim);
        setQuery("");
      }
    } else if (e.key === "Backspace" && !query && selected.length) {
      emit(selected.slice(0, -1));
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, marginBottom: 8 }}>
        {meta.hint} Type to search — related suggestions appear as you type, even for new words.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => setActiveGroup("all")}
          style={filterChipStyle(t, activeGroup === "all")}
        >
          All
        </button>
        {(meta.groups || []).map((g) => (
          <button
            key={g.label}
            type="button"
            onClick={() => setActiveGroup(g.label)}
            style={filterChipStyle(t, activeGroup === g.label)}
          >
            {g.label}
          </button>
        ))}
      </div>

      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => toggleOption(opt)}
              title="Remove"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 999,
                border: `1px solid ${t.accent}44`,
                background: t.soft,
                color: t.chip,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: FONT.family,
                cursor: "pointer",
              }}
            >
              {opt}
              <i className="ti ti-x" style={{ fontSize: 12 }} />
            </button>
          ))}
        </div>
      )}

      <div ref={wrapRef} style={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "10px 12px",
            borderRadius: 12,
            boxSizing: "border-box",
            background: open ? "#fff" : `linear-gradient(180deg, ${t.soft}AA 0%, #FFFFFF 70%)`,
            border: `1.5px solid ${open ? t.accent : `${t.accent}40`}`,
            boxShadow: open ? `0 0 0 3px ${t.soft}` : `inset 3px 0 0 ${t.accent}`,
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          }}
        >
          <i className="ti ti-search" style={{ fontSize: 15, color: t.accent, flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={selected.length ? "Search to add more…" : meta.placeholder}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 13.5,
              fontWeight: 600,
              fontFamily: FONT.family,
              color: C.text,
              minWidth: 0,
            }}
          />
          {remoteLoading ? (
            <span style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, whiteSpace: "nowrap" }}>
              Suggesting…
            </span>
          ) : null}
          <span
            aria-hidden
            onClick={() => {
              setOpen((v) => !v);
              inputRef.current?.focus();
            }}
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              background: t.soft,
              color: t.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${t.accent}33`,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <i className={`ti ti-chevron-${open ? "up" : "down"}`} style={{ fontSize: 14, fontWeight: 700 }} />
          </span>
        </div>

        {open &&
          menuBox &&
          createPortal(
            <>
              <div
                style={{ position: "fixed", inset: 0, zIndex: 1400 }}
                onClick={() => setOpen(false)}
              />
              <div
                role="listbox"
                style={{
                  position: "fixed",
                  zIndex: 1401,
                  left: menuBox.left,
                  width: menuBox.width,
                  top: menuBox.top,
                  bottom: menuBox.bottom,
                  maxHeight: menuBox.maxHeight,
                  background: "#fff",
                  borderRadius: 14,
                  border: `1.5px solid ${t.accent}44`,
                  boxShadow: `0 18px 48px ${t.accent}22, 0 8px 24px rgba(15,23,42,0.12)`,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: "10px 14px",
                    background: `linear-gradient(135deg, ${t.soft} 0%, #FFFFFF 90%)`,
                    borderBottom: `1px solid ${t.accent}28`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 99,
                      background: t.accent,
                      boxShadow: `0 0 0 3px ${t.soft}`,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: t.chip,
                      letterSpacing: 0.04,
                      textTransform: "uppercase",
                      fontFamily: FONT.family,
                    }}
                  >
                    {meta.label}
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, color: C.muted }}>
                    {remoteLoading ? "Finding ideas…" : `${menuRows.length} options`}
                  </span>
                </div>

                <div style={{ overflowY: "auto", padding: 6, flex: 1 }}>
                  {menuRows.length === 0 && (
                    <div style={{ padding: "14px 12px", fontSize: 13, color: C.muted, fontWeight: 600 }}>
                      {qTrim
                        ? remoteLoading
                          ? "Looking for related suggestions…"
                          : `No matches — press Enter to add “${qTrim}”`
                        : "No options in this category"}
                    </div>
                  )}
                  {menuRows.map((row, idx) => {
                    const isSel =
                      (row.type === "option" || row.type === "recommend") &&
                      selected.includes(row.label);
                    const isActive = idx === activeIdx;
                    const isCustom = row.type === "custom";
                    const isRec = row.type === "recommend";
                    const prevGroup = idx > 0 ? menuRows[idx - 1].group : null;
                    const showGroup =
                      row.group &&
                      row.group !== prevGroup &&
                      (idx === 0 || menuRows[idx - 1].type === "custom" || row.group !== prevGroup);
                    return (
                      <React.Fragment key={`${row.type}:${row.group}:${row.label}`}>
                        {showGroup && (
                          <div
                            style={{
                              padding: "8px 12px 4px",
                              fontSize: 10.5,
                              fontWeight: 800,
                              color: C.muted,
                              letterSpacing: 0.04,
                              textTransform: "uppercase",
                            }}
                          >
                            {row.group}
                          </div>
                        )}
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSel}
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => pickRow(row)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            width: "100%",
                            textAlign: "left",
                            padding: "10px 12px",
                            marginBottom: 2,
                            border: "none",
                            borderRadius: 10,
                            cursor: "pointer",
                            background: isSel
                              ? `linear-gradient(90deg, ${t.soft} 0%, ${t.soft}CC 100%)`
                              : isActive
                                ? t.soft
                                : "transparent",
                            color: isCustom || isRec ? t.chip : isSel ? t.chip : C.text,
                            fontWeight: isCustom || isSel || isRec ? 750 : 600,
                            fontSize: 13.5,
                            fontFamily: FONT.family,
                            boxShadow: isSel ? `inset 3px 0 0 ${t.accent}` : "none",
                            transition: "background 0.12s ease",
                          }}
                        >
                          {isCustom && (
                            <i className="ti ti-plus" style={{ fontSize: 15, color: t.accent, flexShrink: 0 }} />
                          )}
                          {isRec && (
                            <i className="ti ti-sparkles" style={{ fontSize: 15, color: t.accent, flexShrink: 0 }} />
                          )}
                          <span style={{ flex: 1, lineHeight: 1.35 }}>{row.display}</span>
                          {isSel && (
                            <i className="ti ti-check" style={{ fontSize: 16, color: t.accent, flexShrink: 0 }} />
                          )}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </>,
            document.body
          )}
      </div>
    </div>
  );
}

function filterChipStyle(t, active) {
  return {
    padding: "5px 10px",
    borderRadius: 999,
    border: `1px solid ${active ? `${t.accent}66` : C.border}`,
    background: active ? t.soft : C.surface,
    color: active ? t.chip : C.muted,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: FONT.family,
    cursor: "pointer",
  };
}
